import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout, getCurrentUser, switchCompany } from '../utils/api';
import { toast } from 'sonner';
import { 
  Wallet, TrendingUp, Tags, FileText, LogOut, Menu, X, 
  Settings as SettingsIcon, Shield, Building2, ChevronDown, Check
} from 'lucide-react';

export const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const currentUser = getCurrentUser();

  const hasMultipleCompanies = currentUser?.companies && currentUser.companies.length > 1;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchCompany = async (companyId) => {
    if (companyId === currentUser?.active_company_id) {
      setCompanyDropdownOpen(false);
      return;
    }
    
    try {
      setSwitching(true);
      const data = await switchCompany(companyId);
      toast.success(`Switched to ${data.user.active_company_name}`);
      setCompanyDropdownOpen(false);
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Failed to switch company');
    } finally {
      setSwitching(false);
    }
  };

  const navItems = [
    { path: '/dashboard', icon: TrendingUp, label: 'Dashboard' },
    { path: '/wallets', icon: Wallet, label: 'Wallets' },
    { path: '/transactions', icon: FileText, label: 'Transactions' },
    { path: '/categories', icon: Tags, label: 'Categories' },
    { path: '/reports', icon: FileText, label: 'Reports' },
    { path: '/settings', icon: SettingsIcon, label: 'Settings' }
  ];

  // Add Admin link for super admins
  const adminNavItems = currentUser?.role === 'super_admin' 
    ? [{ path: '/admin', icon: Shield, label: 'Admin' }]
    : [];

  const isActive = (path) => location.pathname === path;

  const CompanySwitcher = ({ isMobile = false }) => {
    if (!hasMultipleCompanies) {
      return currentUser?.active_company_name ? (
        <p className={`text-xs text-emerald-600 flex items-center gap-1 ${isMobile ? '' : 'truncate'}`}>
          <Building2 className="w-3 h-3" />
          {currentUser.active_company_name}
        </p>
      ) : null;
    }

    return (
      <div className="relative">
        <button
          onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
          disabled={switching}
          className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 transition-colors"
          data-testid="company-switcher"
        >
          <Building2 className="w-3 h-3" />
          <span className="truncate max-w-[120px]">{currentUser?.active_company_name}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${companyDropdownOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {companyDropdownOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setCompanyDropdownOpen(false)}
            />
            <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[180px]">
              <p className="px-3 py-1 text-xs text-stone-500 font-medium">Switch Company</p>
              {currentUser?.companies?.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSwitchCompany(company.id)}
                  disabled={switching}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-stone-50 ${
                    company.id === currentUser.active_company_id ? 'text-emerald-700 font-medium' : 'text-stone-700'
                  }`}
                  data-testid={`switch-to-${company.id}`}
                >
                  <span className="truncate">{company.name}</span>
                  {company.id === currentUser.active_company_id && (
                    <Check className="w-4 h-4 text-emerald-600" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-stone-200 bg-white">
        <div className="flex flex-col flex-grow pt-8 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-6 mb-8">
            <h1 className="text-2xl font-bold text-emerald-900 tracking-tight">FinanceGarden</h1>
          </div>
          <nav className="flex-1 px-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                    isActive(item.path)
                      ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                      : 'text-stone-600 hover:bg-stone-50 hover:text-emerald-900'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Admin Section */}
            {adminNavItems.length > 0 && (
              <>
                <div className="pt-4 mt-4 border-t border-stone-200">
                  <p className="px-4 text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">Admin</p>
                </div>
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                        isActive(item.path)
                          ? 'bg-purple-50 text-purple-900 border border-purple-200'
                          : 'text-stone-600 hover:bg-stone-50 hover:text-purple-900'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" strokeWidth={1.5} />
                      {item.label}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
          
          <div className="px-4 pt-4 border-t border-stone-200">
            <div className="px-4 py-3 mb-2 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-medium text-stone-900 truncate">{currentUser?.name || 'User'}</p>
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  currentUser?.role === 'super_admin' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {currentUser?.role === 'super_admin' ? 'Admin' : 'Manager'}
                </span>
              </div>
              <CompanySwitcher />
              <p className="text-xs text-stone-500 truncate mt-0.5">{currentUser?.phone}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-stone-600 hover:text-orange-800 hover:bg-stone-50 rounded-lg transition-all"
            >
              <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-stone-200">
        <div className="flex items-center justify-between p-4">
          <div>
            <h1 className="text-xl font-bold text-emerald-900">FinanceGarden</h1>
            <CompanySwitcher isMobile />
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-stone-600 hover:text-emerald-900"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="px-4 pb-4 space-y-1 border-t border-stone-200">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg ${
                    isActive(item.path)
                      ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                      : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg ${
                    isActive(item.path)
                      ? 'bg-purple-50 text-purple-900 border border-purple-200'
                      : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
            <div className="pt-2 mt-2 border-t border-stone-200">
              <div className="px-4 py-2">
                <p className="text-sm font-medium text-stone-900">{currentUser?.name}</p>
                <p className="text-xs text-stone-500">{currentUser?.phone}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 text-sm font-medium text-orange-800 hover:bg-orange-50 rounded-lg"
              >
                <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Logout
              </button>
            </div>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        <main className="py-6 lg:py-8 px-4 sm:px-6 lg:px-8 mt-16 lg:mt-0">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-stone-200">
        <nav className="flex justify-around py-2">
          {[...navItems.slice(0, 4), ...(currentUser?.role === 'super_admin' ? adminNavItems : [navItems[5]])].map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center px-3 py-2 text-xs font-medium rounded-lg ${
                  isActive(item.path) 
                    ? item.path === '/admin' ? 'text-purple-900' : 'text-emerald-900' 
                    : 'text-stone-500'
                }`}
              >
                <Icon className="w-5 h-5 mb-1" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
